import _ from 'lodash'
import {
  getContractsCountByProjectQuery,
  getContractsLandsQuery,
  getContractsQuery,
  getContractQuery,
  createContractMutation,
  deleteContractMutation,
  updateContractMutation,
  getContractInstallmentsByContractQuery,
  createContractInstallmentMutation,
  deleteContractInstallmentMutation,
  updateContractInstallmentMutation,
  getContractInstallmentGroupsByContractQuery,
  createContractInstallmentGroupMutation,
  updateContractInstallmentGroupMutation,
  getContractInstallmentQuery,
  getFirstContractInstallmentWithPixQrCodeQuery,
  getContractCustomersQuery,
  createContractCustomerMutation,
  updateContractCustomerMutation,
  deleteContractCustomerMutation
} from '~graphql/contracts.graphql'

export default {
  getContracts({ state, dispatch }, { filter, offset, first, orderBy }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    return dispatch('getItems', {
      state,
      loader: 'contracts:get',
      modelName: 'contracts',
      query: getContractsQuery,
      resultPath: 'contracts.nodes',
      totalPath: 'contracts.totalCount',
      totalModelName: 'contractsTotalCount',
      variables: {
        filter: mergedFilter,
        offset,
        first,
        orderBy
      }
    })
  },
  getLandContracts({ state, dispatch }, landId) {
    const filter = {
      landId: {
        equalTo: landId
      }
    }
    return dispatch('getItems', {
      state,
      loader: 'contracts:get',
      modelName: 'landContracts',
      query: getContractsQuery,
      resultPath: 'contracts.nodes',
      variables: {
        filter
      }
    })
  },
  getActiveContractsByLandId({ state, dispatch }, { landId, excludeContractId }) {
    const filter = {
      landId: {
        equalTo: landId
      },
      id: {
        notEqualTo: excludeContractId
      },
      status: {
        notIn: ['CANCELED', 'AUTO_CANCELED', 'TERMINATED', 'TERMINATED_BY_CLAIM', 'FINISHED', 'TRANSFERRED']
      }
    }
    return dispatch('getItems', {
      state,
      loader: 'contracts:get',
      modelName: 'activeContractsByLand',
      query: getContractsQuery,
      resultPath: 'contracts.nodes',
      variables: {
        filter
      }
    })
  },
  getContractsCountByProject({ state, dispatch }, { filter }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    return dispatch('getItems', {
      state,
      loader: 'contractsCountByProject:get',
      modelName: 'contractsCountByProject',
      query: getContractsCountByProjectQuery,
      resultPath: 'contracts.groupedAggregates',
      variables: {
        filter: mergedFilter
      }
    })
  },
  getContractsLands({ state, dispatch }, { filter }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    return dispatch('getItems', {
      state,
      loader: 'contractLands:get',
      modelName: 'contractLands',
      query: getContractsLandsQuery,
      resultPath: 'contracts.nodes',
      variables: {
        filter: mergedFilter
      }
    })
  },
  getFirstContractInstallmentWithPixQrCode({ state, dispatch }, contractId) {
    return dispatch('getItems', {
      state,
      loader: 'firstInstallment:get',
      query: getFirstContractInstallmentWithPixQrCodeQuery,
      resultPath: 'contractInstallments.nodes',
      variables: {
        contractId
      }
    })
  },
  getContractById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      loader: 'contract:get',
      modelName: 'selectedContract',
      query: getContractQuery,
      resultPath: 'contract',
      useCache: false
    })
  },
  getContractCustomers({ state, dispatch }, contractId) {
    const filter = {
      contractId: {
        equalTo: contractId
      }
    }

    return dispatch('getItems', {
      state,
      modelName: 'contractCustomers',
      loader: 'contractCustomers:get',
      query: getContractCustomersQuery,
      variables: {
        filter
      },
      resultPath: 'contractCustomers.nodes',
      useCache: false
    })
  },

  createContract({ state, dispatch }, contract) {
    return dispatch('createItem', {
      item: { contract },
      state,
      loader: 'contract:create',
      addTo: ['contracts'],
      mutation: createContractMutation,
      resultPath: 'createContract.contract'
    })
  },
  updateContract({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContract',
      addTo: ['contracts'],
      loader: 'contract:update',
      mutation: updateContractMutation,
      resultPath: 'updateContract.contract'
    })
  },
  deleteContract({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContract',
      removeFrom: ['contracts'],
      loader: 'contract:update',
      mutation: deleteContractMutation,
      resultPath: 'updateContract.contract'
    })
  },
  setSelectedContract({ state, commit }, selectedContract) {
    commit('SET_ITEM', {
      state,
      item: selectedContract,
      modelName: 'selectedContract'
    })
  },
  clearSelectedContract({ state, commit }) {
    commit('SET_ITEM', {
      item: {},
      state,
      modelName: 'selectedContract'
    })
  },

  // Contract Installments

  getAllContractInstallmentsByContractId({ state, dispatch }, contractId) {
    return dispatch('getItems', {
      state,
      loader: 'contractInstallments:get',
      modelName: 'contractInstallments',
      query: getContractInstallmentsByContractQuery,
      resultPath: 'contractInstallments.nodes',
      variables: {
        contractId
      }
    })
  },
  getContractInstallmentById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      loader: 'contractInstallment:get',
      modelName: 'selectedContractInstallment',
      query: getContractInstallmentQuery,
      resultPath: 'contractInstallment',
      useCache: false
    })
  },
  createContractInstallment({ state, dispatch }, contractInstallment) {
    return dispatch('createItem', {
      item: { contractInstallment },
      state,
      loader: 'contractInstallment:persist',
      addTo: ['contractInstallments'],
      mutation: createContractInstallmentMutation,
      resultPath: 'createContractInstallment.contractInstallment'
    })
  },
  updateContractInstallment({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContractInstallment',
      addTo: ['contractInstallments'],
      loader: 'contractInstallment:persist',
      mutation: updateContractInstallmentMutation,
      resultPath: 'updateContractInstallment.contractInstallment'
    })
  },
  deleteContractInstallment({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContractInstallment',
      removeFrom: ['contractInstallments'],
      loader: 'contractInstallment:update',
      mutation: deleteContractInstallmentMutation,
      resultPath: 'updateContractInstallment.contractInstallment'
    })
  },
  setSelectedContractInstallment({ state, commit }, selectedContractInstallment) {
    commit('SET_ITEM', {
      state,
      item: selectedContractInstallment,
      modelName: 'selectedContractInstallment'
    })
  },
  clearSelectedContractInstallment({ state, commit }) {
    commit('SET_ITEM', {
      item: {},
      state,
      modelName: 'selectedContractInstallment'
    })
  },

  // Contract Installment Groups

  getAllContractInstallmentGroupsByContractId({ state, dispatch }, contractId) {
    return dispatch('getItems', {
      state,
      loader: 'contractInstallmentGroups:get',
      modelName: 'contractInstallmentGroups',
      query: getContractInstallmentGroupsByContractQuery,
      resultPath: 'contractInstallmentGroups.nodes',
      variables: {
        contractId
      }
    })
  },
  createContractInstallmentGroup({ state, dispatch }, contractInstallmentGroup) {
    return dispatch('createItem', {
      item: { contractInstallmentGroup },
      state,
      loader: 'contractInstallmentGroup:persist',
      addTo: ['contractInstallmentGroups'],
      mutation: createContractInstallmentGroupMutation,
      resultPath: 'createContractInstallmentGroup.contractInstallmentGroup'
    })
  },
  updateContractInstallmentGroup({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContractInstallmentGroup',
      addTo: ['contractInstallmentGroups'],
      loader: 'contractInstallmentGroup:persist',
      mutation: updateContractInstallmentGroupMutation,
      resultPath: 'updateContractInstallmentGroup.contractInstallmentGroup'
    })
  },
  setSelectedContractInstallmentGroup({ state, commit }, selectedContractInstallmentGroup) {
    commit('SET_ITEM', {
      state,
      item: selectedContractInstallmentGroup,
      modelName: 'selectedContractInstallmentGroup'
    })
  },
  clearSelectedContractInstallmentGroup({ state, commit }) {
    commit('SET_ITEM', {
      item: {},
      state,
      modelName: 'selectedContractInstallmentGroup'
    })
  },
  // Contract Customers
  createContractCustomer({ state, dispatch }, { contractCustomer }) {
    return dispatch('createItem', {
      item: { contractCustomer },
      state,
      loader: 'contractCustomer:persist',
      addTo: ['contractCustomers'],
      mutation: createContractCustomerMutation,
      resultPath: 'createContractCustomer.contractCustomer'
    })
  },
  updateContractCustomer({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContractCustomer',
      addTo: ['contractCustomers'],
      loader: 'contractCustomer:persist',
      mutation: updateContractCustomerMutation,
      resultPath: 'updateContractCustomer.contractCustomer'
    })
  },
  deleteContractCustomer({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedContractCustomer',
      removeFrom: ['contractCustomers'],
      loader: 'contractCustomer:delete',
      mutation: deleteContractCustomerMutation,
      resultPath: 'updateContractCustomer.contractCustomer'
    })
  }
}
